//@ts-check
/* istanbul ignore-next */
const initFB = (_fb, appId) => {
    _fb.init({
        appId,
        xfbml: true,
        cookie: true,
        version: 'v12.0',
    });

    _fb.AppEvents.logPageView();
    return _fb;
};

const bootstrapFacebookScriptTag = function(d, s, id, cb) {
    const fjs = d.getElementsByTagName(s)[0];
    const js = d.createElement(s);
    js.id = id;
    js.type = 'text/javascript';
    js.onload = cb;
    js.src = '//connect.facebook.net/en_US/sdk.js';
    fjs.parentNode.insertBefore(js, fjs);
};

// _getSDK :: String -> Promise FacebookSDK
const _getSDK = appId =>
    new Promise(resolve => {
        const previousScriptTag = document.getElementById('facebook-jssdk');
        let bootstrapFn = (a, b, c, callback) => callback && callback();

        if (!previousScriptTag || !window.FB) {
            // script tag not loaded on the page, load it!
            previousScriptTag && previousScriptTag.parentNode.removeChild(previousScriptTag);
            bootstrapFn = bootstrapFacebookScriptTag;
            if (appId) {
                window.fbAsyncInit = function() {
                    resolve(initFB(window.FB, appId));
                };
            }
            bootstrapFn(document, 'script', 'facebook-jssdk');
        } else {
            bootstrapFn(document, 'script', 'facebook-jssdk', () =>
                resolve(appId ? initFB(window.FB, appId) : initFB(window.FB)),
            );
        }
    });

const setupGetSDKClosure = function() {
    let cachedFB, cachedFBAppId;
    const stashFB = _fb => {
        cachedFB = _fb;
        return _fb;
    };
    return function(appId) {
        return new Promise((resolve, reject) => {
            if (cachedFB && cachedFBAppId === appId) {
                resolve(cachedFB);
            } else {
                cachedFBAppId = appId;
                _getSDK(appId)
                    .then(stashFB)
                    .then(resolve, reject);
            }
        });
    };
};

/**
 * @type {function(string): Promise<facebook.FacebookStatic>}
 */
export const getSDK = setupGetSDKClosure();

// getPageAccessToken :: FacebookSDK -> ([FBPermission], Int) -> Promise String
export const getPageAccessToken = pageId => FB =>
    new Promise((resolve, reject) => {
        return FB.api('/me/accounts', 'GET', {fields: 'id,name,tasks,access_token', limit: '1000'}, response => {
            if (response.error) {
                return reject(response.error);
            }
            if (response.data && response.data.length) {
                const page = response.data.find(o => o.id === pageId);
                if (page) {
                    if (page.access_token) {
                        return resolve(page.access_token);
                    } else {
                        return reject(`Page access token was not available for pageId ${pageId}`);
                    }
                }
                return reject(`Page matching pageId ${pageId} does not exist for logged in facebook user`);
            }
        });
    });

// AuthResponse: https://developers.facebook.com/docs/facebook-login/web#checklogin
// getLoginStatus :: FacebookSDK -> Promise AuthResponse
export const getLoginStatus = FB => new Promise(resolve => FB.getLoginStatus(resolve));

export const getFacebookAuth = FB =>
    getLoginStatus(FB).then(({authResponse} = {}) => authResponse || Promise.reject('Not logged into facebook'));

// isLoggedIn :: String -> Boolean
export const isLoggedIn = status => status === 'connected';

// getIsLoggedIn :: FacebookSDK -> Promise Boolean
export const getIsLoggedIn = FB => getLoginStatus(FB).then(isLoggedIn);

// login :: string -> FacebookSDK -> Promise FacebookSDK
export const login = permissions => FB =>
    new Promise((resolve, reject) => {
        FB.login(
            response => {
                if (response.status === 'connected') {
                    resolve(response);
                } else if (response.status === 'not_authorized') {
                    reject('The signed in facebook user is not authorized to use associated Facebook App');
                } else {
                    reject(`Login to Facebook did not complete`);
                }
            },
            {scope: permissions},
        );
        return FB;
    });
